import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import _ from 'lodash'
import React from 'react'

import { convertDoseNumberToWord } from '../../../../../../components/modals/AddEmarMedicationModal/utils'
import {
  generateWeekTitle,
  getDates,
  splitDatesIntoWeeks
} from './dateFunctions'
import { renderDoseColumn, renderHourColumn } from './renderColumnFunctions'

dayjs.extend(isSameOrBefore)

/**
@description Formats medication data for table

@param {object} medication. The medication to be converted.

@param {object} rowDisplaySettings. Object containing boolean toggles for
displaying e.g. gutter row, medication row etc.

@param {string} chartStartDate. The start date. E.g. 12-12-2020

@param {string} chartEndDate. The end date. E.g. 15-12-2020

@returns {array} Formatted medication
*/

export const convertMedicationForTable = ({
  medication,
  rowDisplaySettings = {},
  chartStartDate,
  chartEndDate
}) => {
  const {
    id,
    name,
    startDate: medStartDate,
    endDate: medEndDate,
    roundTimings,
    freqDaysInWeek,
    freqInterval,
    freqUnit,
    quantityType,
    PRN
  } = medication
  const {
    displayGutter = true,
    displayMedName = true,
    isMedLinkActive = false
  } = rowDisplaySettings

  const gutterRow = displayGutter
    ? [{ key: `${id} gutter`, type: 'gutter', hour: '' }]
    : []
  const medicationName = displayMedName
    ? [
        {
          key: `${id} name`,
          type: 'medication',
          hour: name,
          medication,
          isMedLinkActive
        }
      ]
    : []

  let dayjsChartStartDate = dayjs(chartStartDate, 'DD-MM-YYYY')
  const dayjsChartEndDate = dayjs(chartEndDate, 'DD-MM-YYYY')
  const chartDates = []

  while (dayjsChartStartDate.isSameOrBefore(dayjsChartEndDate)) {
    chartDates.push(dayjsChartStartDate.format('DD-MM-YYYY'))
    dayjsChartStartDate = dayjsChartStartDate.add(1, 'day')
  }

  const rounds = roundTimings.filter(Boolean).map((roundDetails) => {
    const dose =
      convertDoseNumberToWord(roundDetails.dose, quantityType) +
      (roundDetails.altDose
        ? ` or ${convertDoseNumberToWord(roundDetails.altDose, quantityType)}`
        : '') +
      (quantityType === 'ml' ? ' ml' : '')

    const doseStyles = roundDetails.altDose ? styles.altDose : null
    const roundsByDate = _.keyBy(roundDetails.rounds, 'date')

    const allDatesRounds = chartDates.map(
      (date) => roundsByDate[date] || { date }
    )

    return {
      dose: <div style={doseStyles}>{dose}</div>,
      hour: roundDetails?.time || '',
      key: `${roundDetails.emarRoundTiming} ${id}`,
      freqDaysInWeek,
      freqInterval,
      freqUnit,
      medStartDate,
      medEndDate,
      PRN,
      ..._.keyBy(allDatesRounds, 'date')
    }
  })

  return [...gutterRow, ...medicationName, ...rounds]
}

/**
@description Filters and sorts the medications. Filters out any
discontinued medications that fall outside the date interval,
and sorts the medications first into active and deleted, then
each sublist into alphabetical order.

@param {array} medications. The medications.

@param {string} startDate. The chart start date. E.g. 12-12-2020

@param {string} endDate. The chart end date. E.g. 15-12-2020

@returns {array} The filtered and sorted medications.
*/

export const filterMedications = (medications, startDate, endDate) => {
  const filteredMedications = _.filter(medications, (medication) => {
    // Filters out meds where either:
    //  1. The med end date is before the chart start date
    //  2. The med start date is after the chart end date
    // if (dayjs(medication.startDate, 'DD-MM-YYYY').isAfter(endDate)
    // || dayjs(medication.endDate, 'DD-MM-YYYY').isBefore(startDate)) {
    //     return false
    // }

    if (!medication.deleted) return true

    const { roundTimings } = medication

    if (!roundTimings) return false

    const allRounds = _.compact(
      roundTimings.flatMap((roundTiming) =>
        roundTiming ? roundTiming.rounds : []
      )
    )

    if (!allRounds.length) return false

    const finalRoundDate = dayjs(
      _.orderBy(allRounds, 'timeLogged', 'desc')[0].timeLogged
    )

    // TODO: Fix this for edge cases - what if a med has a start date before the start date
    // and an end date after the end date?
    const isInDateInterval = finalRoundDate.isAfter(
      dayjs(startDate, 'DD-MM-YYYY').startOf('day')
    )
    // && finalRoundDate.isBefore(dayjs(endDate, 'DD-MM-YYYY').endOf('day'))

    return isInDateInterval
  })

  const sortedMedications = _.orderBy(filteredMedications, ['deleted', 'name'])

  return sortedMedications
}

/**
@description Generates the table data

@param {array} medications. The medications.

@param {object} rowDisplaySettings. Object containing boolean toggles for
displaying e.g. gutter row, medication row etc.

@param {string} startDate. The start date. E.g. 12-12-2020

@param {string} endDate. The end date. E.g. 15-12-2020

@returns {array} The sorted and converted medications.
*/

export const getMARChartTableData = (
  medications,
  rowDisplaySettings = {},
  startDate,
  endDate
) => {
  const filteredMedications = filterMedications(medications, startDate, endDate)

  return _.flatten(
    filteredMedications.map((medication, index) => {
      if (rowDisplaySettings.displayFirstGutter !== undefined && index === 0) {
        return convertMedicationForTable({
          medication,
          rowDisplaySettings: {
            ...rowDisplaySettings,
            displayGutter: rowDisplaySettings.displayFirstGutter
          },
          chartStartDate: startDate,
          chartEndDate: endDate
        })
      }

      return convertMedicationForTable({
        medication,
        rowDisplaySettings,
        chartStartDate: startDate,
        chartEndDate: endDate
      })
    })
  )
}

// Generate column functions

/**
@description Generates left hand columns for table

@returns {object} The hour and dose columns.
*/

const generateLeftColumns = () => ({
  width: '180px',
  children: [
    {
      title: 'Hour',
      fixed: 'left',
      dataIndex: 'hour',
      key: 'hour',
      width: '90px',
      render: renderHourColumn
    },
    {
      title: 'Dose',
      dataIndex: 'dose',
      key: 'dose',
      width: '90px',
      fixed: 'left',
      render: renderDoseColumn
    }
  ]
})

/**
@description Generates date columns for table

@param {string} startDate. The start date e.g. 27-01-2020

@param {string} endDate. The end date e.g. 29-01-2020

@returns {object} The date columns.
*/

const generateDateColumns = (startDate, endDate, isPDF) => {
  const dates = getDates(startDate, endDate, isPDF)
  const datesByWeek = splitDatesIntoWeeks(dates)

  const dateColumns = datesByWeek.map((week, index) => {
    if (index === datesByWeek.length - 1) {
      return {
        children: week,
        className: 'final-column',
        title: generateWeekTitle(week)
      }
    }

    return {
      title: generateWeekTitle(week),
      children: week,
      width: '360px'
    }
  })

  return dateColumns
}

/**
@description Generates all columns for table

@param {string} startDate. The start date e.g. 27-01-2020

@param {string} endDate. The end date e.g. 29-01-2020

@returns {object} The table columns.
*/

export const generateTableColumns = (startDate, endDate, isPDF) => {
  const columns = _.concat(
    generateLeftColumns(),
    generateDateColumns(startDate, endDate, isPDF)
  )

  return columns
}

/**
@description Gets the start date for the table. If the med start
date is less than 2 weeks before the med end date, the table
start date is set to be 2 weeks pre the end date.

@param {string} startDate. The start date. E.g. 12-12-2020

@param {string} endDate. The end date.

@returns {string} TableStartDate. The table start date.
*/

export const getTableStartDate = (startDate, endDate) => {
  if (
    dayjs(endDate, 'DD-MM-YYYY').diff(dayjs(startDate, 'DD-MM-YYYY'), 'days') <
    14
  ) {
    return dayjs(endDate, 'DD-MM-YYYY')
      .subtract(14, 'days')
      .format('DD-MM-YYYY')
  }
  return startDate
}

export const groupChartData = (chartData) => {
  // Split data into pages
  // Each page can have a max of ~25 rows.

  let groups = []
  let group = []
  let rowCount = 0
  const MAX_ROWS_PER_GROUP = 25

  _.orderBy(chartData, 'medication.name').forEach((row, index) => {
    let newRows = 1 // Medication title counts as one row

    row.roundTimings.forEach((round) => {
      newRows += 1
    })

    rowCount += newRows

    // Check if the group row count exceeds the limit
    if (rowCount > MAX_ROWS_PER_GROUP) {
      groups = [...groups, group]
      group = [row] // start a new group
      rowCount = newRows // reset count.
      // newRows is needed to ensure that we don't loose track of the number of new rows
    } else {
      // Add row to group
      group = [...group, row]
    }
    rowCount += 1

    // Push the group anyway if it's the last row
    if (chartData.length - 1 === index) {
      groups = [...groups, group]
    }
  })

  return groups
}

/**
@description Gets the last signed round date for a medication. Used
for discontinued/deleted meds in the medication screen, to set the
end date in the table.

@param {array} roundTimings. The round timings for the medication.

@returns {string} finalRoundDate.
*/

export const getLastSignedRoundDate = (roundTimings) => {
  const allRounds = _.flatten(
    roundTimings.map((roundTiming) => roundTiming.rounds)
  )

  if (!allRounds.length) return dayjs()

  const finalRoundDate = dayjs(
    _.orderBy(allRounds, 'timeLogged', 'desc')[0].timeLogged
  )

  return finalRoundDate
}

const styles = {
  altDose: {
    fontSize: 12
  }
}
